import {
  useBuildingBulkDelete,
  useBuildings,
  useExportBuilding,
} from "@/api/building";
import { TableDefault } from "@/components/table/table-default";
import { TableForm } from "@/components/table/table-form";
import {
  DataConfig,
  useDefaultDataConfig,
} from "@/components/table/useDefaultDataConfig";
import { generateCustomSpec } from "@/utils/helper";
import {
  swalDelete,
  swalError,
  swalErrorDefault,
  swalSuccess,
} from "@/utils/lib/swal";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ROLE } from "@/utils/constant";
import { useCurrentUser } from "../auth/useCurrentUser";
import { DownloadButton } from "@/components/ui/button";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { nullElement } from "@/components/null-element";

const customAdminSpec = generateCustomSpec({
  path: "",
  fields: [
    {
      label: "Category Bizpass",
      name: "categoryBizpass",
    },
    {
      label: "Dwelling Type",
      name: "dwellingType",
    },
  ],
});

const customNonAdminSpec = generateCustomSpec({
  path: "",
  canBulk: false,
  fields: [
    {
      label: "Category Bizpass",
      name: "categoryBizpass",
    },
    {
      label: "Dwelling Type",
      name: "dwellingType",
    },
  ],
});

const HeaderElement = ({ dataConfig }: { dataConfig: DataConfig }) => {
  const { mutate: exportList, isPending: isDownloading } = useExportBuilding();

  const handleExport = () => {
    const params = {
      ...dataConfig,
      limit: undefined,
      page: undefined,
    };

    exportList(
      {
        config: params,
      },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  };
  return (
    <div className="flex items-center justify-end gap-2">
      <DownloadButton onClick={handleExport} loading={isDownloading} />
    </div>
  );
};

export function BuildingTable() {
  const [selectedRow, setSelectedRow] = useState<number[]>([]);
  const { dataConfig, setDataConfig } = useDefaultDataConfig();
  const tableForm = useForm();

  const { data: buildings } = useBuildings(dataConfig);
  const { mutate, isPending } = useBuildingBulkDelete();

  const { roles } = useCurrentUser();
  const isValidator = hasCommonElement(roles, [
    ROLE.VALIDATOR_DOCUMENT_OFFICER,
    ROLE.VENDOR,
  ]);
  const isSysAdminView = roles.includes(ROLE.SYS_ADMIN_VIEW);
  const isVendorView = roles.includes(ROLE.VENDOR_VIEW);

  function handleBulkDelete() {
    swalDelete.fire({
      preConfirm: () => {
        mutate(
          { ids: selectedRow as unknown[] as string[] },
          {
            onSuccess: () => {
              swalSuccess.fire({
                text: "Building has been deleted",
              });
            },
            onError: (error) => {
              swalError.fire({
                text: error.message,
              });
            },
            onSettled: () => {
              setSelectedRow([]);
            },
          },
        );
      },
    });
  }

  const isViewOnly = isValidator || isSysAdminView || isVendorView;

  return (
    <>
      <TableForm
        setDataConfig={setDataConfig}
        form={tableForm}
        createPath={isViewOnly ? null : "/master-data/category-bizzpass/create"}
        selectedIds={selectedRow as unknown as string[]}
        handleBulkDelete={isViewOnly ? null : handleBulkDelete}
        isPendingDelete={isPending}
      />
      <TableDefault
        spec={isValidator ? customNonAdminSpec : customAdminSpec}
        data={buildings}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        dataConfig={dataConfig}
        setDataConfig={setDataConfig}
        searchFilterByColumns={true}
        headerElement={<HeaderElement dataConfig={dataConfig} />}
        customButtonEdit={
          isSysAdminView || isVendorView ? nullElement : undefined
        }
      />
    </>
  );
}
